import axios from 'axios';
import {
    Container,
    Box,
    Grid,
    Typography,
    CardContent,
    CardActions,
    Button,
    CircularProgress,
    Paper,
    Divider,
    Chip,
    useTheme,
    IconButton,
    Fade,
    TextField,
    InputAdornment, useMediaQuery
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuth } from "firebase/auth";
import SchoolIcon from '@mui/icons-material/School';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QuizIcon from '@mui/icons-material/Quiz';
import MicIcon from '@mui/icons-material/Mic';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useLocation } from 'react-router-dom';
import ClassIcon from '@mui/icons-material/Class';
import { useLanguage } from './LanguageContext';
import {useEffect, useState} from "react";
import SearchIcon from '@mui/icons-material/Search';

export default function MyClassDetails() {
    const [loading, setLoading] = useState(true);
    const [classroom, setClassroom] = useState(null);
    const [quizStatuses, setQuizStatuses] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const { classId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const location = useLocation();
    const classInfo = location.state?.classroom;
    const { t } = useLanguage();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (classInfo?.metadata?.title) {
            document.title = `${classInfo.metadata.title} | ClassCapture`;
        } else {
            document.title = `${t.classDetails} | ClassCapture`;
        }
    }, [classroom, t, t.classDetails, classInfo]);

    // Récupération des détails de la classe et de ses ressources
    useEffect(() => {
        const fetchClassDetails = async () => {
            try {
                const auth = getAuth();
                const token = await auth.currentUser.getIdToken();
                const url = `https://classroomservices-470732867211.us-central1.run.app/api/classroom/${classId}/resources`;
                const response = await axios.get(url, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setClassroom(response.data);
            } catch (error) {
                console.error('Error fetching class details', error);
            } finally {
                setLoading(false);
            }
        };

        fetchClassDetails();
    }, [classId]);

    // Pour chaque ressource de type quiz, récupérer le statut via l'API /status/:quiz_id
    useEffect(() => {
        const fetchQuizStatus = async (quizId) => {
            try {
                const auth = getAuth();
                const token = await auth.currentUser.getIdToken();
                const url = `https://quizservices-470732867211.us-central1.run.app/api/quizzes/status/${quizId}`;
                const response = await fetch(url, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                const data = await response.json();
                if (response.ok && data.status) {
                    return data.status;
                } else {
                    return "Not taken";
                }
            } catch (error) {
                console.error("Error fetching quiz status for", quizId, error);
                return "Not taken";
            }
        };

        if (classroom && classroom.resources) {
            const quizResources = classroom.resources.filter(r => r.type === "quiz");

            Promise.all(
                quizResources.map(async (quizRes) => {
                    const status = await fetchQuizStatus(quizRes.metadata.id);
                    return { id: quizRes.metadata.id, status };
                }),
            ).then(results => {
                const statuses = {};
                results.forEach(({ id, status }) => {
                    statuses[id] = status;
                });

                setQuizStatuses(statuses);
            });
        }
    }, [classroom]);

    const handleBack = () => {
        navigate('/classes');
    };

    const handleQuizAttempt = (quizId) => {
        navigate(`/attempt/${quizId}`);
    };

    const handleViewResults = (quizId) => {
        navigate(`/result/${quizId}`);
    };

    const handleViewTranscription = (transcriptionId) => {
        navigate(`/transcription/${transcriptionId}`);
    };

    const getStatusChip = (status) => {
        switch (status) {
            case "Taken":
                return <Chip
                    label={t.completed}
                    size="small"
                    color="success"
                    sx={{
                        height: 20,
                        '& .MuiChip-label': {
                            px: 1,
                            fontSize: '0.7rem'
                        }
                    }}
                />;
            case "In progress":
                return <Chip
                    label={t.inProgress}
                    size="small"
                    color="warning"
                    sx={{
                        height: 20,
                        '& .MuiChip-label': {
                            px: 1,
                            fontSize: '0.7rem'
                        }
                    }}
                />;
            case "Pending for Evaluation":
                return <Chip
                    label={t.pendingEvaluation}
                    size="small"
                    color="info"
                    sx={{
                        height: 20,
                        '& .MuiChip-label': {
                            px: 1,
                            fontSize: '0.7rem'
                        }
                    }}
                />;
            default:
                return <Chip
                    label={t.notStarted}
                    size="small"
                    variant="outlined"
                    sx={{
                        height: 20,
                        '& .MuiChip-label': {
                            px: 1,
                            fontSize: '0.7rem'
                        }
                    }}
                />;
        }
    };

    const gradientBackground = {
        background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
    };

    // Filtrer les ressources en fonction de la recherche
    const filteredResources = classroom?.resources?.filter(res =>
        res.metadata.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (res.metadata.description && res.metadata.description.toLowerCase().includes(searchTerm.toLowerCase()))
    ) || [];

    if (loading) {
        return (
            <Container sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                flexDirection: 'column',
                gap: 1
            }}>
                <SchoolIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                <Typography variant="subtitle1" component="h1" color="primary" fontWeight={450}>
                    ClassCapture
                </Typography>
                <CircularProgress color="primary" size={24} />
            </Container>
        );
    }

    if (!classroom) {
        return (
            <Container sx={{ py: 4 }}>
                <Paper
                    elevation={1}
                    sx={{
                        p: 3,
                        textAlign: 'center',
                        borderRadius: 1,
                        bgcolor: 'background.paper'
                    }}
                >
                    <InfoOutlinedIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                    <Typography variant="h6" color="error" gutterBottom>
                        {t.errorLoading}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        {t.errorLoadingDescription}
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                        sx={{ mt: 1 }}
                    >
                        {t.backToClasses}
                    </Button>
                </Paper>
            </Container>
        );
    }

    return (
        <Container component="main" maxWidth="lg" sx={{ py: 2 }}>
            {/* Header with gradient background and search */}
            <Fade in={true} timeout={800}>
                <Paper
                    elevation={1}
                    sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 1,
                        ...gradientBackground,
                        color: 'black'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton
                                size="small"
                                onClick={handleBack}
                                sx={{ color: 'black', mr: 1 }}
                            >
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                            <ClassIcon sx={{ fontSize: 24, mr: 1 }} />
                            <Typography variant="h6" component="h1" fontWeight={450} noWrap>
                                {classInfo.metadata?.title || t.classDetails}
                            </Typography>
                        </Box>

                        <TextField
                            placeholder={t.searchResources}
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{
                                width: isMobile ? '100px' : '180px',
                                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                borderRadius: 1,
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'white',
                                    },
                                },
                                '& input': {
                                    color: 'white',
                                    padding: '6px 10px',
                                    fontSize: '0.875rem',
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon sx={{ color: 'white', fontSize: 18 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </Paper>
            </Fade>

            {/* Class information card */}
            <Fade in={true} timeout={1000}>
                <Paper
                    elevation={1}
                    sx={{
                        p: 2,
                        mb: 3,
                        borderRadius: 1,
                        backgroundColor: 'background.paper'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16, mr: 1, color: theme.palette.primary.main }} />
                        <Typography variant="subtitle1" fontWeight={450}>
                            {t.aboutThisClass}
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        {classInfo.metadata?.description || t.noDescription}
                    </Typography>

                    {classInfo.metadata?.timestamps?.createdAt && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <EventNoteIcon sx={{ fontSize: 14, mr: 1, color: theme.palette.text.secondary }} />
                            <Typography variant="caption" color="text.secondary">
                                {t.created}: {new Date(classInfo.metadata.timestamps.createdAt).toLocaleDateString()}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Fade>

            {/* Resources Section */}
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" component="h2" fontWeight={450}>
                    {t.availableResources}
                </Typography>
                <Divider sx={{ ml: 2, flexGrow: 1 }} />
            </Box>

            {/* Resources Grid */}
            {filteredResources.length > 0 ? (
                <Fade in={true} timeout={1200}>
                    <Grid container spacing={2}>
                        {filteredResources.map((res) => (
                            <Grid item xs={12} sm={6} md={4} key={res.metadata.id}>
                                <Paper
                                    elevation={1}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        transition: 'transform 0.15s, box-shadow 0.15s',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: 2,
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            p: 1,
                                            background: res.type === "quiz" ?
                                                `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)` :
                                                `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                                            color: 'black',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {res.type === "quiz" ? (
                                                <QuizIcon sx={{ mr: 1, fontSize: 16 }} />
                                            ) : (
                                                <MicIcon sx={{ mr: 1, fontSize: 16 }} />
                                            )}
                                            <Typography variant="body2" fontWeight={450} noWrap>
                                                {res.metadata.title || "Untitled Resource"}
                                            </Typography>
                                        </Box>

                                        {res.type === "quiz" && (
                                            getStatusChip(quizStatuses[res.metadata.id])
                                        )}
                                    </Box>

                                    <CardContent sx={{ flexGrow: 1, p: 1.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{
                                            mb: 1,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            lineHeight: 1.3
                                        }}>
                                            {res.metadata.description || "No description available"}
                                        </Typography>

                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mt: 'auto'
                                        }}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ fontSize: '0.65rem' }}
                                            >
                                                {res.metadata.timestamps?.createdAt ?
                                                    new Date(res.metadata.timestamps.createdAt).toLocaleDateString() :
                                                    "No date"}
                                            </Typography>
                                        </Box>
                                    </CardContent>

                                    <Divider />

                                    <CardActions sx={{ p: 0 }}>
                                        {res.type === "quiz" ? (
                                            (quizStatuses[res.metadata.id] === "Not taken" ||
                                                quizStatuses[res.metadata.id] === "In progress" ||
                                                !quizStatuses[res.metadata.id]) ? (
                                                <Button
                                                    fullWidth
                                                    onClick={() => handleQuizAttempt(res.metadata.id)}
                                                    endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                                                    sx={{
                                                        p: 0.75,
                                                        textTransform: 'none',
                                                        fontWeight: 450,
                                                        justifyContent: 'space-between',
                                                        fontSize: '0.75rem',
                                                        color: theme.palette.secondary.main
                                                    }}
                                                >
                                                    {quizStatuses[res.metadata.id] === "In progress" ? t.continueQuiz : t.takeQuiz}
                                                </Button>
                                            ) : (
                                                <Button
                                                    fullWidth
                                                    onClick={() => handleViewResults(res.metadata.id)}
                                                    endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                                                    sx={{
                                                        p: 0.75,
                                                        textTransform: 'none',
                                                        fontWeight: 450,
                                                        justifyContent: 'space-between',
                                                        fontSize: '0.75rem',
                                                        color: theme.palette.secondary.main
                                                    }}
                                                >
                                                    {t.viewResults}
                                                </Button>
                                            )
                                        ) : (
                                            res.type === "transcription" && (
                                                <Button
                                                    fullWidth
                                                    onClick={() => handleViewTranscription(res.metadata.id)}
                                                    endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                                                    sx={{
                                                        p: 0.75,
                                                        textTransform: 'none',
                                                        fontWeight: 450,
                                                        justifyContent: 'space-between',
                                                        fontSize: '0.75rem'
                                                    }}
                                                >
                                                    {t.viewCourse}
                                                </Button>
                                            )
                                        )}
                                    </CardActions>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Fade>
            ) : (
                <Fade in={true} timeout={1200}>
                    <Paper
                        elevation={1}
                        sx={{
                            p: 2,
                            textAlign: 'center',
                            borderRadius: 1,
                            bgcolor: 'background.paper'
                        }}
                    >
                        {searchTerm ? (
                            <>
                                <InfoOutlinedIcon sx={{ fontSize: 24, color: 'text.secondary', mb: 1 }} />
                                <Typography variant="body2" gutterBottom>
                                    {t.noMatchingResources}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t.noMatchingResourcesDesc}
                                </Typography>
                            </>
                        ) : (
                            <>
                                <InfoOutlinedIcon sx={{ fontSize: 24, color: 'text.secondary', mb: 1 }} />
                                <Typography variant="body2" gutterBottom>
                                    {t.noResources}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t.noResourcesDescription}
                                </Typography>
                            </>
                        )}
                    </Paper>
                </Fade>
            )}
        </Container>
    );
}